import React from 'react';

import { Box, Container, Link, Typography } from '@mui/material';

const emailLink = <Link href='mailto:info@scaledtech.consulting'>info@scaledtech.consulting</Link>;

function PrivacyNotice() {
  return (
    <Container
      maxWidth='md'
      sx={{ pt: { xs: 4, md: 7 }, overflowWrap: 'anywhere' }}
    >
      <Typography
        component='h1'
        variant='h3'
        sx={{ fontWeight: 800, color: '#0B2545', fontSize: { xs: '2rem', md: '3rem' } }}
      >
        Privacy notice
      </Typography>
      <Typography sx={{ mt: 2 }}>
        Scaled Tech Consulting LLC operates this website. This notice explains how information is
        used when you visit the site or contact us through it.
      </Typography>

      <Box
        component='section'
        sx={{ mt: 4 }}
      >
        <Typography
          component='h2'
          variant='h5'
        >
          Contact inquiries
        </Typography>
        <Typography sx={{ mt: 2 }}>
          You can browse the site without creating an account. If you use the contact form, we ask
          for your name, email address, and message, and you can optionally share the kind of work
          and timing. We use these details to respond to your inquiry and follow up on the matter
          you raise. Please do not include sensitive or confidential information in the form.
        </Typography>
        <Typography sx={{ mt: 2 }}>
          The form uses Cloudflare Turnstile to help prevent automated submissions. Your browser
          interacts with Cloudflare, and the site checks a verification token with Cloudflare before
          accepting the form. If verification does not finish, you can contact us directly at{' '}
          {emailLink}.
        </Typography>
        <Typography sx={{ mt: 2 }}>
          The site uses Cloudflare&apos;s email service to send the inquiry to our email address.
          Our receiving mailbox is provided through Apple iCloud Mail. If you email us directly,
          your message may also pass through Cloudflare&apos;s email routing service and the other
          email services involved in delivering it.
        </Typography>
        <Typography sx={{ mt: 2 }}>
          If you book a call, the booking is handled by Cal.com under its own privacy policy; we
          receive the details you enter there.
        </Typography>
      </Box>

      <Box
        component='section'
        sx={{ mt: 4 }}
      >
        <Typography
          component='h2'
          variant='h5'
        >
          Website operation and analytics
        </Typography>
        <Typography
          component='h3'
          variant='h6'
          sx={{ mt: 3 }}
        >
          Hosting and security
        </Typography>
        <Typography sx={{ mt: 2 }}>
          Cloudflare helps serve and protect the website. In providing those services, Cloudflare
          may process technical information about a request, such as an IP address, browser
          information, and request or security data. Cloudflare may use security cookies or similar
          technologies when needed to protect or operate its services.
        </Typography>
        <Typography
          component='h3'
          variant='h6'
          sx={{ mt: 3 }}
        >
          Website analytics
        </Typography>
        <Typography sx={{ mt: 2 }}>
          We use Cloudflare Web Analytics to understand page visits and website performance. It can
          report information such as the page visited, referring site, country, browser, device
          type, and page-load performance. Our current setting is configured to exclude visitor data
          in the European Union; Cloudflare says the measurement script is not injected for visitors
          from the EU under that setting. This exclusion does not cover every country in Europe.
        </Typography>
        <Typography sx={{ mt: 2 }}>
          To see which parts of the site are useful, we count clicks on a few buttons, such as
          requesting an assessment or opening the capability statement. Your browser sends only the
          button&apos;s name to our own server; we don&apos;t set cookies or store identifiers or IP
          addresses with these counts.
        </Typography>
        <Typography
          component='h3'
          variant='h6'
          sx={{ mt: 3 }}
        >
          Error and performance reporting
        </Typography>
        <Typography sx={{ mt: 2 }}>
          We use a self-hosted Sentry service to help identify website errors and investigate
          performance. Reports contain limited technical information, such as error locations and
          timing measurements. We configure the site to exclude contact-form contents and URL query
          values from these reports. Connections to this service pass through Cloudflare.
        </Typography>
        <Typography
          component='h3'
          variant='h6'
          sx={{ mt: 3 }}
        >
          Session recordings
        </Typography>
        <Typography sx={{ mt: 2 }}>
          We record a sample of visits and may also save a buffered recording when a website error
          occurs. These simplified recordings include page structure, visited public pages, pointer
          movements, clicks and scrolling. Text and form inputs are masked, although masked text
          length may remain visible; images and other media are blocked. Recordings stop when the
          contact page or an unrecognized page is reached. A recording session identifier is stored
          in the browser&apos;s session storage for the tab.
        </Typography>
        <Typography
          component='h3'
          variant='h6'
          sx={{ mt: 3 }}
        >
          Accessibility display preferences
        </Typography>
        <Typography sx={{ mt: 2 }}>
          The website&apos;s accessibility display options apply in the current browser tab until it
          is reloaded or closed. They are not saved as account preferences or sent to us.
        </Typography>
      </Box>

      <Box
        component='section'
        sx={{ mt: 4 }}
      >
        <Typography
          component='h2'
          variant='h5'
        >
          How long information is kept
        </Typography>
        <Typography sx={{ mt: 2 }}>
          We retain inquiry correspondence to respond, follow up, and handle any resulting business
          or applicable recordkeeping needs. The time can depend on the inquiry and whether it
          becomes part of a client or other record. Messages may also be held by our email providers
          or in backups, according to the systems involved. Deleting an active-mailbox message does
          not necessarily remove every backup copy immediately.
        </Typography>
      </Box>

      <Box
        component='section'
        sx={{ mt: 4 }}
      >
        <Typography
          component='h2'
          variant='h5'
        >
          Requests and contact
        </Typography>
        <Typography sx={{ mt: 2 }}>
          For questions about this notice or to request access to, correction of, or deletion of
          information relating to your use of the website, email {emailLink}. We will consider
          requests under applicable law and may need to verify your identity before acting. Rights
          and any exceptions depend on the law that applies to the request. Depending on the
          information and applicable law, we may need to retain certain records or rely on a service
          provider to address information held in its systems.
        </Typography>
        <Typography sx={{ mt: 2 }}>
          Links to other websites are governed by those sites&apos; own privacy notices.
        </Typography>
      </Box>
    </Container>
  );
}

export default PrivacyNotice;
